import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, resolveAvatar, fileUrl, thumbUrl } from "@/lib/api";
import { MediaTile } from "@/components/MediaTile";
import { MediaLightbox } from "@/components/MediaLightbox";
import { UploadDialog } from "@/components/UploadDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Search, Upload, Star, Flame, TrendingUp, Footprints, Users, Image as ImageIcon,
  Video, Play, LogOut, User as UserIcon, Pencil, Shield, Images, Loader2, ArrowRight, MessageSquare, MapPin, Layers, Sparkles, Menu, Inbox,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { NotificationsBell } from "@/components/NotificationsBell";
import { AISearchDialog } from "@/components/AISearchDialog";
import { activeSection, parseBrowseParams } from "@/lib/nav";
import { SiteNav, MobileNavMenu } from "@/components/SiteNav";

const HERO_IMAGE = "https://customer-assets-lqy194kg.emergentagent.net/job_photo-vote-hub-1/artifacts/pb9akg8r_betty.png";

// Main discovery nav. Photos / Videos are browse FILTERS (pills below the hero, deep-linkable via ?type=photos|videos),
// not top-level destinations; Submit and Requests live in My Page + the avatar menu.
const NAV = [
  { l: "Latest", key: "latest", act: (s) => s.reset() },
  { l: "Top Rated", key: "top", act: (s) => s.setSort("top") },
  { l: "Trending", key: "trending", act: (s) => s.setSort("trending") },
];

const Browse = () => {
  const { user, login, logout, openPersona, requireAction, requireUpload, canUpload, openAuth } = useAuth();
  const navigate = useNavigate();

  const [source, setSource] = useState("all");
  const initialParams = parseBrowseParams(window.location.search);
  const [type, setType] = useState(initialParams.type);   // /browse?type=photos|videos deep links
  const [sort, setSort] = useState(initialParams.sort);   // /browse?sort=top|trending deep links
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [forceGrid, setForceGrid] = useState(false);
  const resultsRef = useRef(null);

  const viewAll = (s) => {
    setSort(s);
    setForceGrid(true);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const [rows, setRows] = useState({ latest: [], trending: [], top: [] });
  const [grid, setGrid] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const location = useLocation();
  React.useEffect(() => {
    const openId = new URLSearchParams(location.search).get("open");
    if (openId) {
      setSelected(openId);
      navigate("/browse", { replace: true });  // clear the param so refresh doesn't reopen
    }
  }, [location.search]);  // eslint-disable-line react-hooks/exhaustive-deps
  // URL ⇄ filter sync: Back/Forward/reload restore the filter; user clicks push a history entry.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("open")) return;
    const p = parseBrowseParams(location.search);
    if (p.type !== type) setType(p.type);
    if (p.sort !== sort) setSort(p.sort);
  }, [location.search]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (new URLSearchParams(location.search).get("open")) return;
    const sp = new URLSearchParams();
    if (type !== "all") sp.set("type", type);
    if (sort !== "latest") sp.set("sort", sort);
    const next = sp.toString() ? `?${sp.toString()}` : "";
    const cur = new URLSearchParams(location.search);
    const invalid = (cur.has("type") && parseBrowseParams(location.search).type !== cur.get("type")) || (cur.has("sort") && parseBrowseParams(location.search).sort !== cur.get("sort"));
    if (next !== location.search) navigate(`/browse${next}`, { replace: invalid });  // unknown values normalise without a history entry
  }, [type, sort]);  // eslint-disable-line react-hooks/exhaustive-deps
  const active = activeSection(location.pathname, location.search);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = () => api.get("/messages/unread").then((r) => alive && setUnread(r.data.count)).catch(() => {});
    load();
    const id = setInterval(load, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  const isDefault = source === "all" && type === "all" && sort === "latest" && !debounced && !forceGrid;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/media", { params: { sort: "recent", limit: 12 } }),
        api.get("/media", { params: { sort: "trending", limit: 12 } }),
        api.get("/media", { params: { sort: "top", limit: 12 } }),
      ]);
      setRows({ latest: a.data, trending: b.data, top: c.data });
    } finally { setLoading(false); }
  }, []);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      params.sort = sort === "latest" ? "recent" : sort === "most" ? "most" : sort;
      if (source !== "all") params.source = source;
      if (type !== "all") params.type = type === "photos" ? "image" : "video";
      if (debounced) params.q = debounced;
      const res = await api.get("/media", { params });
      setGrid(res.data);
    } finally { setLoading(false); }
  }, [source, type, sort, debounced]);

  useEffect(() => {
    if (isDefault) loadRows();
    else loadGrid();
  }, [isDefault, loadRows, loadGrid]);

  const refresh = () => { if (isDefault) loadRows(); else loadGrid(); };
  const reset = () => { setSource("all"); setType("all"); setSort("latest"); setQuery(""); setDebounced(""); setForceGrid(false); };
  const navState = { reset, setSort, setType };
  const isDefaultSortView = !debounced && !forceGrid;  // a text search is its own view — no section lit

  const avatarSrc = resolveAvatar(user?.avatar_url) || user?.picture;

  const Pill = ({ active, onClick, icon: Icon, children, testid }) => (
    <button data-testid={testid} onClick={onClick}
      className={cn("flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm border transition-colors whitespace-nowrap",
        active ? "bg-primary text-primary-foreground border-primary font-medium" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40")}>
      {Icon && <Icon className="w-3.5 h-3.5" />} {children}
    </button>
  );

  return (
    <div className="min-h-screen relative z-10 bg-background">
      {/* HEADER */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 h-16 flex items-center gap-4 xl:gap-8">
          <button data-testid="browse-logo" onClick={() => navigate("/")} className="flex items-center gap-2.5 shrink-0">
            <img src="/logo-icon.png" alt="" className="h-7 w-auto" />
            <span className="font-heading text-xl font-semibold text-primary hidden sm:block">BareFootage</span>
          </button>

          <div className="mx-auto"><SiteNav user={user} /></div>

          <div className="flex items-center gap-2.5 sm:gap-3 ml-auto lg:ml-0 shrink-0">
            <button data-testid="ai-search-open" onClick={() => setAiOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 text-primary px-3 h-9 text-sm font-medium hover:bg-primary/10 transition-colors whitespace-nowrap">
              <Sparkles className="w-4 h-4" /> <span className="hidden sm:inline">AI Search</span>
            </button>
            <MobileNavMenu user={user} />
            {user && <NotificationsBell />}
            {user && (
              <button data-testid="browse-messages-btn" onClick={() => navigate("/messages")}
                className="relative h-9 w-9 inline-flex items-center justify-center rounded-full border border-border hover:border-primary hover:text-primary transition-colors">
                <MessageSquare className="w-4 h-4" />
                {unread > 0 && <span className="absolute -top-1 -right-1 text-[9px] font-bold bg-primary text-primary-foreground rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">{unread}</span>}
              </button>
            )}
            {user ? (
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button data-testid="browse-user-menu" className="outline-none"
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => setMenuOpen((o) => !o)}>
                    <Avatar className="w-9 h-9 border border-border hover:border-primary transition-colors">
                      <AvatarImage src={avatarSrc} />
                      <AvatarFallback className="bg-secondary text-xs">{(user.display_name || user.name)?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-popover border-border">
                  <DropdownMenuLabel className="flex flex-col">
                    <span className="text-sm">{user.display_name || user.name}</span>
                    <span className="text-xs text-muted-foreground font-normal truncate">{user.email}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="browse-menu-profile" onClick={() => navigate("/profile")}><UserIcon className="w-4 h-4 mr-2" /> My Page</DropdownMenuItem>}
                  <DropdownMenuItem data-testid="browse-menu-messages" onClick={() => navigate("/messages")}><MessageSquare className="w-4 h-4 mr-2" /> Messages{unread > 0 && <span className="ml-auto text-[10px] font-bold bg-primary text-primary-foreground rounded-full px-1.5">{unread}</span>}</DropdownMenuItem>
                  <DropdownMenuItem data-testid="browse-menu-persona" onClick={openPersona}><Pencil className="w-4 h-4 mr-2" /> Edit Persona</DropdownMenuItem>
                  {user.is_admin && <DropdownMenuItem data-testid="browse-menu-admin" onClick={() => navigate("/admin")}><Shield className="w-4 h-4 mr-2" /> Admin Studio</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="browse-menu-logout" onClick={logout} className="text-destructive focus:text-destructive"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-4">
                <button data-testid="browse-signin-link" onClick={() => openAuth("login")}
                  className="hidden sm:inline text-sm font-semibold text-muted-foreground hover:text-primary transition-colors">Sign In</button>
                <button data-testid="browse-signin" onClick={() => openAuth("register")}
                  className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-base font-semibold shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-transform">Join Free</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 pt-8">
        <div className="grid lg:grid-cols-[1fr_360px] gap-5">
          <div className="relative rounded-2xl overflow-hidden min-h-[300px] flex items-end p-8 border border-white/5">
            <img src={HERO_IMAGE} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 brightness-[0.5] sm:hidden" />
            <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-contain sm:object-cover object-center brightness-[0.92]" />
            <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/55 to-transparent" />
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative max-w-lg">
              <div className="flex items-center gap-2 text-primary mb-3 text-xs tracking-[0.2em] uppercase font-bold">
                <TrendingUp className="w-4 h-4" /> Top Rated by the Community
              </div>
              <h1 className="font-gothic italic text-4xl sm:text-5xl font-medium tracking-tight leading-[0.95]">
                Rank Your<br />Favourite <span className="text-primary not-italic">Feet</span>
              </h1>
              <p className="text-muted-foreground mt-3">Submit your photos and videos. Rate others. Discover the top-ranked content.</p>
              <div className="flex flex-wrap gap-3 mt-5">
                <button data-testid="hero-view-rate" onClick={() => setSort("top")}
                  className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 font-medium hover:-translate-y-0.5 transition-transform">
                  <Star className="w-4 h-4 fill-current" /> View & Rate
                </button>
                {(!user || canUpload) && <button data-testid="hero-submit" onClick={() => requireUpload() && setUploadOpen(true)}
                  className="flex items-center gap-2 rounded-full bg-secondary border border-border px-5 py-2.5 font-medium hover:border-primary/40 transition-colors">
                  <Upload className="w-4 h-4" /> Submit Content
                </button>}
              </div>
            </motion.div>
          </div>

          <div className="grid grid-rows-2 gap-5">
            <button data-testid="card-originals" onClick={() => { setSource("barefootage"); }}
              className="text-left rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors flex gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0"><Footprints className="w-5 h-5 text-primary" /></div>
              <div>
                <h3 className="font-heading text-lg">BareFootage Originals</h3>
                <p className="text-xs text-muted-foreground mt-1">Exclusive content posted by BareFootage. Curated, high quality, and always original.</p>
                <span className="text-primary text-sm font-medium mt-2 inline-flex items-center gap-1">Explore Originals <ArrowRight className="w-3.5 h-3.5" /></span>
              </div>
            </button>
            <button data-testid="card-community" onClick={() => { setSource("community"); }}
              className="text-left rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors flex gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-emerald-400" /></div>
              <div>
                <h3 className="font-heading text-lg">Community Uploads</h3>
                <p className="text-xs text-muted-foreground mt-1">Content submitted by our amazing community. Authentic. Diverse. Ranked by people like you.</p>
                <span className="text-primary text-sm font-medium mt-2 inline-flex items-center gap-1">Explore Community <ArrowRight className="w-3.5 h-3.5" /></span>
              </div>
            </button>
          </div>
        </div>

        {/* SEARCH */}
        <div className="relative mt-6 max-w-3xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="search-input"
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search photos, videos, descriptions or tags…"
            className="w-full rounded-full bg-card border border-border pl-11 pr-4 py-3 text-sm outline-none focus:border-primary/50 transition-colors"
          />
        </div>

        {/* FILTER PILLS + SORT */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mt-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Pill testid="pill-all" active={source === "all" && type === "all"} onClick={() => { setSource("all"); setType("all"); }}>All</Pill>
            <Pill testid="pill-barefootage" icon={Footprints} active={source === "barefootage"} onClick={() => setSource(source === "barefootage" ? "all" : "barefootage")}>BareFootage</Pill>
            <Pill testid="pill-community" icon={Users} active={source === "community"} onClick={() => setSource(source === "community" ? "all" : "community")}>Community</Pill>
            <Pill testid="pill-photos" icon={ImageIcon} active={type === "photos"} onClick={() => setType(type === "photos" ? "all" : "photos")}>Photos</Pill>
            <Pill testid="pill-videos" icon={Video} active={type === "videos"} onClick={() => setType(type === "videos" ? "all" : "videos")}>Videos</Pill>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-full bg-secondary/40 border border-border w-fit">
            {[{ v: "latest", l: "Latest" }, { v: "trending", l: "Trending" }, { v: "top", l: "Top Rated" }, { v: "most", l: "Most Rated" }].map((s) => (
              <button key={s.v} data-testid={`sort-${s.v}`} onClick={() => setSort(s.v)}
                className={cn("px-3.5 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap",
                  sort === s.v ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:text-foreground")}>
                {s.l}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* EXPLORE MAP PREVIEW */}
      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 mt-6">
        <button data-testid="browse-map-preview" onClick={() => navigate("/map")}
          className="w-full text-left rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-colors flex items-stretch group">
          <div className="p-6 flex-1">
            <div className="flex items-center gap-2 text-primary text-xs tracking-[0.2em] uppercase font-bold"><MapPin className="w-4 h-4" /> View Map</div>
            <h3 className="font-heading text-2xl mt-2">Explore by Location</h3>
            <p className="text-sm text-muted-foreground mt-1">Discover uploads and creators from around the world. Open the map to explore.</p>
            <span className="inline-flex items-center gap-2 mt-4 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium group-hover:-translate-y-0.5 transition-transform">Open Map <ArrowRight className="w-4 h-4" /></span>
          </div>
          <div className="hidden sm:block w-1/2 relative min-h-[160px] overflow-hidden" style={{ background: "radial-gradient(circle at 30% 40%, rgba(217,162,74,.28), transparent 40%), radial-gradient(circle at 70% 60%, rgba(217,162,74,.22), transparent 35%), #0a0a0a" }}>
            <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(#d9a24a 1px, transparent 1px)", backgroundSize: "22px 22px", opacity: 0.25 }} />
            <div className="absolute inset-0 flex items-center justify-center"><Flame className="w-10 h-10 text-primary/70" /></div>
          </div>
        </button>
      </section>

      {/* CONTENT */}
      <section ref={resultsRef} className="max-w-[1600px] mx-auto px-5 sm:px-8 py-8 scroll-mt-20">
        {loading ? (
          <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : isDefault ? (
          <div className="space-y-10">
            <Row title="New Drops" icon={Flame} items={rows.latest} onOpen={setSelected} onViewAll={() => viewAll("latest")} viewAllTestid="viewall-new-drops" />
            <Row title="Trending Right Now" icon={TrendingUp} items={rows.trending} onOpen={setSelected} onViewAll={() => viewAll("trending")} viewAllTestid="viewall-trending" />
            <Row title="Top Rated" icon={Star} items={rows.top} onOpen={setSelected} onViewAll={() => viewAll("top")} viewAllTestid="viewall-top" />
            <RisingCreatorsRow onOpenProfile={(id) => navigate(`/u/${id}`)} />
            <GalleriesRows onOpen={setSelected} onView={(id) => navigate(`/gallery/${id}`)} />
            {rows.latest.length === 0 && (
              <div className="text-center py-16">
                <p className="text-muted-foreground">Nothing here yet.</p>
                <button onClick={() => requireUpload() && setUploadOpen(true)} className="mt-4 rounded-full bg-primary text-primary-foreground px-5 py-2.5 font-medium">Submit the first post</button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-2xl">Results <span className="text-muted-foreground text-base">({grid.length})</span></h2>
              <button onClick={reset} data-testid="clear-filters" className="text-sm text-primary hover:underline">Clear filters</button>
            </div>
            {grid.length === 0 ? (
              <div className="h-40 flex items-center justify-center text-muted-foreground border border-dashed border-border rounded-xl">No matches.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
                {grid.map((it, i) => <MediaTile key={it.id} item={it} index={i} onOpen={(x) => setSelected(x.id)} />)}
              </div>
            )}
          </>
        )}
      </section>

      <footer className="border-t border-border py-8 mt-6">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-2"><img src="/logo-icon.png" alt="" className="h-5 w-auto" /> © 2026 BareFootage</span>
          <div className="flex items-center gap-5">
            <button className="hover:text-foreground">How It Works</button>
            <button className="hover:text-foreground">Guidelines</button>
            <button className="hover:text-foreground">Terms</button>
            <button className="hover:text-foreground">Privacy</button>
          </div>
        </div>
      </footer>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={refresh} />
      <MediaLightbox mediaId={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={refresh} onNavigate={setSelected} playlist={grid} />
      <AISearchDialog open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
};

const Row = ({ title, icon: Icon, items, onOpen, onViewAll, testid, viewAllTestid }) => {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 font-heading text-2xl"><Icon className="w-5 h-5 text-primary" /> {title}</h2>
        <button data-testid={viewAllTestid} onClick={onViewAll} className="text-sm text-primary hover:underline flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex gap-5 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {items.map((it, i) => (
          <div key={it.id} className="w-[280px] shrink-0 snap-start">
            <MediaTile item={it} index={i} onOpen={(x) => onOpen(x.id)} />
          </div>
        ))}
      </div>
    </div>
  );
};

const RisingCreatorsRow = ({ onOpenProfile }) => {
  const [creators, setCreators] = React.useState([]);
  React.useEffect(() => { api.get("/creators/rising", { params: { limit: 8 } }).then((r) => setCreators(r.data)).catch(() => {}); }, []);
  if (!creators.length) return null;
  return (
    <div data-testid="rising-creators">
      <div className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 font-heading text-2xl"><Flame className="w-5 h-5 text-primary" /> Fresh Faces <span className="text-sm text-muted-foreground font-normal">· rising creators</span></h2>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {creators.map((c) => (
          <button key={c.user_id} data-testid={`rising-${c.user_id}`} onClick={() => onOpenProfile(c.user_id)}
            className="w-[240px] shrink-0 snap-start text-left rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
            <div className="flex items-center gap-3">
              <Avatar className="w-11 h-11 border border-border"><AvatarImage src={resolveAvatar(c.avatar_url)} /><AvatarFallback className="bg-secondary">{c.name?.[0]}</AvatarFallback></Avatar>
              <div className="min-w-0">
                <div className="font-medium truncate flex items-center gap-1">@{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.uploads} uploads · ★ {c.avg_rating || "—"}</div>
              </div>
            </div>
            <div className="flex gap-1.5 mt-3">
              {c.thumbs.map((t) => <div key={t.id} className="w-1/3 aspect-square rounded-lg overflow-hidden bg-black"><img src={thumbUrl(t.file_ref, 200)} alt="" className="w-full h-full object-cover" /></div>)}
            </div>
            <span className="mt-3 inline-flex items-center gap-1 text-xs text-primary font-medium">View gallery <ArrowRight className="w-3 h-3" /></span>
          </button>
        ))}
      </div>
    </div>
  );
};

const GalleriesRows = ({ onOpen, onView }) => {
  const [galleries, setGalleries] = React.useState([]);
  React.useEffect(() => { api.get("/galleries").then((r) => setGalleries(r.data)).catch(() => {}); }, []);
  if (!galleries.length) return null;
  return (
    <>
      {galleries.map((g) => (
        <Row key={g.id} testid={`gallery-row-${g.id}`} viewAllTestid={`gallery-viewall-${g.id}`} title={g.name} icon={Layers} items={g.items} onOpen={onOpen} onViewAll={() => onView(g.id)} />
      ))}
    </>
  );
};

export default Browse;
